import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Search, Filter } from 'lucide-react';
import { neonQuery } from '../services/api';
import { getAccessToken } from '../services/auth';

const BC_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:8888/.netlify/functions/bc-proxy'
  : '/.netlify/functions/bc-proxy';
const bcProxyHeaders = () => {
  const t = getAccessToken();
  const h = { 'Content-Type': 'application/json' };
  if (t) h['Authorization'] = `Bearer ${t}`;
  return h;
};

const SYNC_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:8888/.netlify/functions/bc-price-backfill?mode=diff'
  : '/.netlify/functions/bc-price-backfill?mode=diff';

function fmt$(v) {
  if (v == null || v === '') return '—';
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (isNaN(n)) return '—';
  return '$' + n.toFixed(2);
}

function fmtDelta(oldV, newV) {
  if (oldV == null || newV == null) return null;
  const diff = Number(newV) - Number(oldV);
  if (diff === 0) return null;
  const color = diff > 0 ? '#22c55e' : '#dc2626';
  return <span style={{ color, fontSize: 11, marginLeft: 4 }}>{diff > 0 ? '+' : ''}{diff.toFixed(2)}</span>;
}

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function PriceHistory() {
  const [tab, setTab] = useState('changes'); // 'changes' | 'historical'
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [skuFilter, setSkuFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [days, setDays] = useState(7);
  const [webhookStatus, setWebhookStatus] = useState(null);
  const [registering, setRegistering] = useState(false);

  // Historical tab state
  const [histRows, setHistRows] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [histSku, setHistSku] = useState('');
  const today = new Date().toISOString().slice(0, 10);
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [histStart, setHistStart] = useState(thirtyDaysAgo);
  const [histEnd, setHistEnd] = useState(today);
  const [changedOnly, setChangedOnly] = useState(false);

  const load = useCallback(async (forceSync = false) => {
    setLoading(true);
    try {
      // Force a BC→Neon diff sync first so we catch any webhook misses
      if (forceSync) {
        try { await fetch(SYNC_URL, { method: 'POST' }); }
        catch(e) { console.warn('sync failed', e); }
      }
      const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
      const res = await neonQuery('price_history_list', {
        sku: skuFilter || undefined,
        source: sourceFilter || undefined,
        since,
        limit: 500,
      });
      setRows(Array.isArray(res) ? res : []);
    } catch(e) {
      console.error('price_history_list failed', e);
      setRows([]);
    } finally { setLoading(false); }
  }, [skuFilter, sourceFilter, days]);

  useEffect(() => { load(false); }, [load]);

  const loadHistorical = useCallback(async () => {
    setHistLoading(true);
    try {
      const res = await neonQuery('price_snapshot_list', {
        start_date: histStart,
        end_date: histEnd,
        sku: histSku || undefined,
        changed_only: changedOnly ? 1 : undefined,
        limit: 2000,
      });
      setHistRows(Array.isArray(res) ? res : []);
    } catch(e) {
      console.error('price_snapshot_list failed', e);
      setHistRows([]);
    } finally { setHistLoading(false); }
  }, [histStart, histEnd, histSku, changedOnly]);

  useEffect(() => { if (tab === 'historical') loadHistorical(); }, [tab, loadHistorical]);

  const checkWebhook = async () => {
    try {
      const res = await fetch(BC_URL, {
        method: 'POST',
        headers: bcProxyHeaders(),
        body: JSON.stringify({ method: 'GET', path: '/v3/hooks' }),
      });
      const data = await res.json();
      const hooks = Array.isArray(data?.data) ? data.data : [];
      const priceHook = hooks.find(h =>
        h.scope === 'store/product/updated' &&
        (h.destination || '').includes('bc-price-webhook')
      );
      setWebhookStatus(priceHook ? { installed: true, id: priceHook.id, destination: priceHook.destination, is_active: priceHook.is_active } : { installed: false });
    } catch(e) {
      setWebhookStatus({ error: e.message });
    }
  };

  useEffect(() => { checkWebhook(); }, []);

  const registerWebhook = async () => {
    setRegistering(true);
    try {
      const destination = 'https://cpx-prototype.netlify.app/.netlify/functions/bc-price-webhook';
      const res = await fetch(BC_URL, {
        method: 'POST',
        headers: bcProxyHeaders(),
        body: JSON.stringify({
          method: 'POST',
          path: '/v3/hooks',
          body: {
            scope: 'store/product/updated',
            destination,
            is_active: true,
          },
        }),
      });
      const data = await res.json();
      if (data?.data?.id) {
        await checkWebhook();
        alert('Webhook registered successfully.');
      } else {
        alert('Failed to register webhook: ' + JSON.stringify(data));
      }
    } catch(e) {
      alert('Error: ' + e.message);
    } finally { setRegistering(false); }
  };

  const sourceBadge = (s) => {
    if (s === 'cpx') return <span style={{ background: '#fff8f5', color: 'var(--orange)', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>CPx</span>;
    if (s === 'external') return <span style={{ background: '#fef3c7', color: '#b45309', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>EXTERNAL</span>;
    return <span style={{ background: '#f3f4f6', color: '#6b7280', padding: '2px 8px', borderRadius: 4, fontSize: 11 }}>{s || '—'}</span>;
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, color: 'var(--navy)' }}>Price History</h1>
          <div style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>
            All BigCommerce price changes — from CPx, the BC admin panel, or any other API client.
          </div>
        </div>
        <button className="btn btn-outline" onClick={() => tab === 'changes' ? load(true) : loadHistorical()} disabled={loading || histLoading}>
          <RefreshCw size={14} style={{ marginRight: 6 }} />{(loading || histLoading) ? 'Syncing…' : 'Refresh'}
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid #e5e7eb', marginBottom: 16 }}>
        {[{ id: 'changes', label: 'Changes' }, { id: 'historical', label: 'Historical' }].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: 600,
              color: tab === t.id ? 'var(--orange)' : '#6b7280',
              borderBottom: tab === t.id ? '2px solid var(--orange)' : '2px solid transparent',
              marginBottom: -2,
            }}
          >{t.label}</button>
        ))}
      </div>

      {tab === 'changes' && <>
      {/* Webhook status banner */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <strong>BigCommerce Webhook: </strong>
            {!webhookStatus && <span style={{ color: '#6b7280' }}>Checking…</span>}
            {webhookStatus?.error && <span style={{ color: '#dc2626' }}>Error: {webhookStatus.error}</span>}
            {webhookStatus?.installed && (
              <span style={{ color: '#16a34a' }}>
                ✓ Installed (ID {webhookStatus.id}{!webhookStatus.is_active && ' — INACTIVE'})
              </span>
            )}
            {webhookStatus && webhookStatus.installed === false && (
              <span style={{ color: '#b45309' }}>Not installed — click Register to enable price change tracking.</span>
            )}
          </div>
          {webhookStatus && !webhookStatus.installed && !webhookStatus.error && (
            <button className="btn btn-primary" onClick={registerWebhook} disabled={registering}>
              {registering ? 'Registering…' : 'Register Webhook'}
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Search size={14} />
            <input
              className="form-input"
              placeholder="SKU or product name"
              value={skuFilter}
              onChange={e => setSkuFilter(e.target.value)}
              style={{ width: 220 }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Filter size={14} />
            <select className="form-select" value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}>
              <option value="">All sources</option>
              <option value="cpx">CPx</option>
              <option value="external">External</option>
            </select>
          </div>
          <select className="form-select" value={days} onChange={e => setDays(Number(e.target.value))}>
            <option value={1}>Last 24h</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <div style={{ marginLeft: 'auto', color: '#6b7280', fontSize: 13 }}>
            {rows.length} change{rows.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      <div className="card">
        <table className="orders-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Product</th>
              <th>SKU</th>
              <th>MSRP</th>
              <th>Sale Price</th>
              <th>Source</th>
              <th>Changed By</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>No price changes in this window.</td></tr>
            )}
            {rows.map(r => (
              <tr key={r.id}>
                <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDate(r.changed_at)}</td>
                <td style={{ fontSize: 13 }}>{r.name || '—'}</td>
                <td style={{ fontSize: 12, fontFamily: 'monospace' }}>{r.sku || '—'}</td>
                <td style={{ fontSize: 13 }}>
                  {r.old_price != null && Number(r.old_price) !== Number(r.new_price) ? (
                    <>
                      <span style={{ textDecoration: 'line-through', color: '#9ca3af' }}>{fmt$(r.old_price)}</span>
                      {' → '}
                      <strong>{fmt$(r.new_price)}</strong>
                      {fmtDelta(r.old_price, r.new_price)}
                    </>
                  ) : fmt$(r.new_price)}
                </td>
                <td style={{ fontSize: 13 }}>
                  {r.old_sale_price != null && Number(r.old_sale_price) !== Number(r.new_sale_price) ? (
                    <>
                      <span style={{ textDecoration: 'line-through', color: '#9ca3af' }}>{fmt$(r.old_sale_price)}</span>
                      {' → '}
                      <strong>{fmt$(r.new_sale_price)}</strong>
                      {fmtDelta(r.old_sale_price, r.new_sale_price)}
                    </>
                  ) : fmt$(r.new_sale_price)}
                </td>
                <td>{sourceBadge(r.source)}</td>
                <td style={{ fontSize: 13 }}>{r.changed_by || <span style={{ color: '#9ca3af' }}>—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>}

      {tab === 'historical' && <>
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-body" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Search size={14} />
              <input
                className="form-input"
                placeholder="SKU or product name"
                value={histSku}
                onChange={e => setHistSku(e.target.value)}
                style={{ width: 220 }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 13, color: '#6b7280' }}>From</span>
              <input type="date" className="form-input" value={histStart} onChange={e => setHistStart(e.target.value)} style={{ width: 150 }} />
              <span style={{ fontSize: 13, color: '#6b7280' }}>to</span>
              <input type="date" className="form-input" value={histEnd} onChange={e => setHistEnd(e.target.value)} style={{ width: 150 }} />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={changedOnly} onChange={e => setChangedOnly(e.target.checked)} />
              Changed only
            </label>
            <button className="btn btn-primary" onClick={loadHistorical} disabled={histLoading}>
              {histLoading ? 'Loading…' : 'Apply'}
            </button>
            <div style={{ marginLeft: 'auto', color: '#6b7280', fontSize: 13 }}>
              {histRows.length} snapshot{histRows.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        <div className="card">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Snapshot Date</th>
                <th>Product</th>
                <th>SKU</th>
                <th>MSRP</th>
                <th>Sale Price</th>
              </tr>
            </thead>
            <tbody>
              {histRows.length === 0 && !histLoading && (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                  No snapshots in this range. Daily snapshots run at 2 AM ET — once the first one captures, data will appear here.
                </td></tr>
              )}
              {histRows.map(r => {
                const showDelta = changedOnly && r.prev_price != null;
                const priceChanged = showDelta && Number(r.prev_price) !== Number(r.price);
                const saleChanged = showDelta && Number(r.prev_sale_price) !== Number(r.sale_price);
                return (
                  <tr key={r.id}>
                    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{(r.snapshot_date || '').slice(0, 10)}</td>
                    <td style={{ fontSize: 13 }}>{r.name || '—'}</td>
                    <td style={{ fontSize: 12, fontFamily: 'monospace' }}>{r.sku || '—'}</td>
                    <td style={{ fontSize: 13 }}>
                      {priceChanged ? (
                        <>
                          <span style={{ textDecoration: 'line-through', color: '#9ca3af' }}>{fmt$(r.prev_price)}</span>
                          {' → '}
                          <strong>{fmt$(r.price)}</strong>
                          {fmtDelta(r.prev_price, r.price)}
                        </>
                      ) : fmt$(r.price)}
                    </td>
                    <td style={{ fontSize: 13 }}>
                      {saleChanged ? (
                        <>
                          <span style={{ textDecoration: 'line-through', color: '#9ca3af' }}>{fmt$(r.prev_sale_price)}</span>
                          {' → '}
                          <strong>{fmt$(r.sale_price)}</strong>
                          {fmtDelta(r.prev_sale_price, r.sale_price)}
                        </>
                      ) : fmt$(r.sale_price)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </>}
    </div>
  );
}
